/**
 * Whether the browser thinks it has a network, as a re-rendering value.
 *
 * `navigator.onLine` is a snapshot, so a screen that reads it once disables a
 * button and never re-enables it when the wifi comes back — which is how a
 * phone that reconnected in the kitchen stays stuck on "you are offline". The
 * two events are the only signal there is.
 *
 * It is a weak signal and deliberately used only where being wrong is cheap:
 * `false` here means "the OS is certain there is no network", which is worth
 * saying on a button, while `true` means nothing stronger than "there is an
 * interface up". Anything that must actually reach the server still has to
 * handle the request failing.
 */

import { useEffect, useState } from "react";

export function useOnline(): boolean {
  const [online, setOnline] = useState(() => navigator.onLine !== false);
  useEffect(() => {
    const up = () => setOnline(true);
    const down = () => setOnline(false);
    window.addEventListener("online", up);
    window.addEventListener("offline", down);
    return () => {
      window.removeEventListener("online", up);
      window.removeEventListener("offline", down);
    };
  }, []);
  return online;
}
